import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { GraduationCap, PlayCircle, FileText, CheckCircle2, Award, Clock, Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { ce } from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/dates';

const typeIcon = (t) => (t === 'document' ? FileText : PlayCircle);
const catColor = (c) => ({
  'Laws & Regs': 'bg-blue-100 text-blue-700',
  'Safety': 'bg-red-100 text-red-700',
  'IPM': 'bg-emerald-100 text-emerald-700',
  'WDO': 'bg-amber-100 text-amber-700',
  'Urban': 'bg-purple-100 text-purple-700',
  'Vertebrate': 'bg-orange-100 text-orange-700',
  'Public Health': 'bg-teal-100 text-teal-700',
}[c] || 'bg-slate-100 text-slate-700');

function daysUntil(iso) {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return d;
}

function openCertificate(comp, name) {
  const w = window.open('', '_blank', 'width=900,height=650');
  if (!w) { toast.error('Allow pop-ups to print the certificate'); return; }
  const date = new Date(comp.completedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  w.document.write(`<!doctype html><html><head><title>CE Certificate</title>
  <style>
    body{font-family:Georgia,serif;margin:0;background:#0d2818;display:flex;align-items:center;justify-content:center;height:100vh}
    .cert{background:#fff;width:760px;padding:56px 64px;border:10px solid #8BC53F;box-sizing:border-box;text-align:center;position:relative}
    .cert:before{content:'';position:absolute;inset:14px;border:2px solid #cfe3a8}
    .kick{letter-spacing:.35em;color:#8BC53F;font-size:12px;font-weight:bold;text-transform:uppercase}
    h1{font-size:34px;margin:14px 0 4px;color:#0d2818}
    .sub{color:#475569;font-size:14px}
    .name{font-size:40px;margin:26px 0 8px;color:#0d2818;border-bottom:2px solid #8BC53F;display:inline-block;padding:0 24px 8px}
    .course{font-size:20px;color:#0d2818;margin:18px 0 4px;font-weight:bold}
    .meta{color:#475569;font-size:14px;margin-top:6px}
    .foot{display:flex;justify-content:space-between;margin-top:42px;font-size:12px;color:#64748b}
    .seal{position:absolute;right:48px;bottom:40px;width:84px;height:84px;border-radius:50%;background:#8BC53F;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:11px;text-align:center;line-height:1.2}
  </style></head><body onload="window.print()">
    <div class="cert">
      <div class="kick">Xpertpests Academy · Continuing Education</div>
      <h1>Certificate of Completion</h1>
      <div class="sub">This certifies that</div>
      <div class="name">${name || comp.techName || ''}</div>
      <div class="sub">has successfully completed</div>
      <div class="course">${comp.courseTitle}</div>
      <div class="meta">${comp.ceHours} CE hour(s) · ${comp.category || 'General'} · ${comp.provider || 'Xpertpests Academy'}</div>
      <div class="foot">
        <div>Completed: ${date}</div>
        <div>Certificate ID: ${comp.certificateId}</div>
      </div>
      <div class="seal">XP<br/>VERIFIED</div>
    </div>
  </body></html>`);
  w.document.close();
}

export default function CEStudentView({ compact = false }) {
  const me = (() => { try { return JSON.parse(localStorage.getItem('pp_user') || '{}'); } catch { return {}; } })();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editLicense, setEditLicense] = useState(false);
  const [lic, setLic] = useState({ licenseNumber: '', licenseType: 'Field Representative', expiryDate: '' });

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([ce.courses(), ce.myProgress()]);
      setCourses(c); setProgress(p);
      setLic({
        licenseNumber: p.profile?.licenseNumber || '',
        licenseType: p.profile?.licenseType || 'Field Representative',
        expiryDate: p.profile?.expiryDate || '',
      });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markComplete = async (course) => {
    setBusy(course.id);
    try {
      const comp = await ce.complete(course.id);
      toast.success(`Completed: ${course.title} (+${course.ceHours} CE hrs)`);
      await load();
      openCertificate(comp, progress?.name || me?.name);
    } catch { toast.error('Could not record completion'); }
    finally { setBusy(null); }
  };

  const saveLicense = async () => {
    try {
      await ce.setProfile(lic);
      toast.success('License info saved');
      setEditLicense(false);
      load();
    } catch { toast.error('Could not save'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const completedIds = new Set(progress?.completedCourseIds || []);
  const completionsByCourse = Object.fromEntries((progress?.completions || []).map(c => [c.courseId, c]));
  const pct = progress?.percent || 0;
  const dleft = daysUntil(progress?.profile?.expiryDate);
  const expirySoon = dleft !== null && dleft <= 60;

  return (
    <div className={`${compact ? 'p-4' : 'p-6 lg:p-8'} space-y-5 max-w-5xl mx-auto`} data-testid="ce-student-view">
      {/* Progress summary */}
      <Card className="p-5 border-slate-200 bg-gradient-to-br from-[#0d2818] to-[#16341f] text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#8BC53F] text-xs font-bold tracking-widest uppercase"><GraduationCap className="w-4 h-4" />Continuing Education</div>
            <div className="text-2xl font-extrabold mt-1">{progress?.earnedHours || 0} <span className="text-white/60 text-lg font-bold">/ {progress?.requiredHours} CE hours</span></div>
            <div className="text-sm text-white/70 mt-1">{progress?.remaining > 0 ? `${progress.remaining} hours remaining this cycle` : 'Requirement met 🎉'}</div>
          </div>
          <div className="relative w-24 h-24" data-testid="ce-progress-ring">
            <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
              <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
              <circle cx="18" cy="18" r="16" fill="none" stroke="#8BC53F" strokeWidth="3" strokeDasharray={`${pct} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-xl font-extrabold">{pct}%</div>
          </div>
        </div>
      </Card>

      {/* License panel */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${expirySoon ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
              {expirySoon ? <AlertTriangle className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
            </div>
            <div>
              <div className="font-bold text-slate-900 text-sm">{lic.licenseType || 'License'} {lic.licenseNumber ? `· #${lic.licenseNumber}` : ''}</div>
              <div className="text-xs text-slate-500">
                {progress?.profile?.expiryDate
                  ? <>Renews {formatDate(progress.profile.expiryDate, { year: 'numeric', month: 'numeric', day: 'numeric' })} {dleft !== null && <span className={expirySoon ? 'text-red-600 font-semibold' : ''}>· {dleft} days</span>}</>
                  : 'Add your license & renewal date'}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditLicense(v => !v)} data-testid="edit-license-btn">{editLicense ? 'Cancel' : 'Edit License'}</Button>
        </div>
        {editLicense && (
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">License #</Label><Input value={lic.licenseNumber} onChange={e => setLic({ ...lic, licenseNumber: e.target.value })} className="mt-1 h-10" data-testid="license-number-input" /></div>
            <div><Label className="text-xs">Type</Label><Input value={lic.licenseType} onChange={e => setLic({ ...lic, licenseType: e.target.value })} className="mt-1 h-10" /></div>
            <div><Label className="text-xs">Renewal date</Label><Input type="date" value={lic.expiryDate ? lic.expiryDate.slice(0, 10) : ''} onChange={e => setLic({ ...lic, expiryDate: e.target.value })} className="mt-1 h-10" data-testid="license-expiry-input" /></div>
            <div className="sm:col-span-3"><Button onClick={saveLicense} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="save-license-btn">Save License Info</Button></div>
          </div>
        )}
      </Card>

      {/* Course catalog */}
      <div>
        <h2 className="font-bold text-slate-900 mb-3 px-1">Course Catalog</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {courses.map(c => {
            const done = completedIds.has(c.id);
            const Ico = typeIcon(c.type);
            return (
              <Card key={c.id} className="p-4 border-slate-200 flex flex-col" data-testid={`course-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${catColor(c.category)} border-0 text-[10px] font-bold`}>{c.category}</Badge>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3 h-3" />{c.ceHours} hr</span>
                  </div>
                  {done && <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1 text-[10px] font-bold"><CheckCircle2 className="w-3 h-3" />Done</Badge>}
                </div>
                <div className="font-bold text-slate-900 mt-2 flex items-center gap-2"><Ico className="w-4 h-4 text-[#8BC53F] flex-shrink-0" />{c.title}</div>
                <p className="text-xs text-slate-500 mt-1 flex-1">{c.description}</p>
                <div className="mt-3">
                  {done ? (
                    <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => openCertificate(completionsByCourse[c.id], progress?.name || me?.name)} data-testid={`certificate-${c.id}`}>
                      <Award className="w-4 h-4 text-[#8BC53F]" />View Certificate
                    </Button>
                  ) : (
                    <Button size="sm" className="w-full bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" disabled={busy === c.id} onClick={() => markComplete(c)} data-testid={`complete-${c.id}`}>
                      {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Mark Complete
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
